import { apiLocalesGetCollection } from '@r/api/generated/client.ts';
import type { GroupedLocalesModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalServerError, isOrvalServiceUnavailableError } from '@r/api/orval-error-helper.ts';
import { MAINTENANCE_CRITICAL_RETRY_COUNT } from '@r/lib/maintenance-critical-api.ts';
import { maintenanceCriticalRetryDelay } from '@r/lib/maintenance-critical-query.ts';
import { triggerServerError } from '@r/lib/server-error-events.ts';

/** Pause entre deux tentatives de chargement i18n */
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Charge `/locales` pour i18next avec la même politique que le boot :
 * 503 → maintenance immédiate ; 500 → retries puis {@link triggerServerError}.
 */
export async function fetchLocalesWithMaintenanceRetry(
  appId: string
): Promise<GroupedLocalesModel> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= MAINTENANCE_CRITICAL_RETRY_COUNT; attempt++) {
    try {
      return await apiLocalesGetCollection(undefined, { headers: { 'App-ID': appId } });
    } catch (error) {
      lastError = error;

      if (isOrvalServiceUnavailableError(error)) {
        triggerServerError('503');
        throw error;
      }

      if (!isOrvalServerError(error)) {
        throw error;
      }

      if (attempt < MAINTENANCE_CRITICAL_RETRY_COUNT) {
        await sleep(maintenanceCriticalRetryDelay(attempt));
        continue;
      }

      triggerServerError('500');
      throw error;
    }
  }

  throw lastError;
}
